<template>
  <div class="company-search w-full">
    <el-popover
      ref="companySearchResult"
      :visible="visible && companyList.length > 0"
      popper-class="company-search__popper"
      :width="poperWidth"
      :show-arrow="false"
    >
      <template #reference>
        <input
          ref="searchInput"
          v-model="inputValue"
          class="th-input th-input-lg w-full text-sm"
          type="text"
          :placeholder="placeholder"
          @click.stop
          @input="handleInput"
          @blur="emit('blur')"
        />
      </template>

      <div>
        <PtList v-if="!loading" :items="companyList" @item-click="chooseItem($event)"></PtList>
        <div v-else class="company-search__loading">
          <PtIcon icon="SolidLoadingBars"></PtIcon>
        </div>
      </div>
    </el-popover>
  </div>
</template>

<script setup lang="ts">
import { debounce, isEmpty } from 'lodash';
import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';
import { PtList, PtIcon } from 'patsnap-biz';
import { ElPopover } from 'element-plus';
import { CommonApis } from '@eureka/services/src/apis/common.api';

const props = withDefaults(
  defineProps<{
    value: string;
    poperWidth: number;
    placeholder: string;
  }>(),
  {
    value: '',
    poperWidth: 453,
    placeholder: '',
  },
);

const visible = ref(false);
const loading = ref(false);
const disabled = ref(true);
const inputValue = ref('');
const companyList = ref([]);
const getCompanyInfoApi = CommonApis.getCompanyInfo;
const companySearchResult = ref();
const searchInput = ref();

const emit = defineEmits(['input', 'blur', 'update:modelValue']);

const loadCompanyList = debounce(async () => {
  if (!inputValue.value) {
    companyList.value = [];
    disabled.value = true;
    return;
  }
  try {
    loading.value = true;
    disabled.value = false;
    companyList.value = await getCompanyInfoApi(inputValue.value);
    loading.value = false;
    if (isEmpty(companyList.value)) disabled.value = true;
  } catch (error) {
    disabled.value = true;
    loading.value = false;
  }
}, 200);

const handleInput = () => {
  emit('input', inputValue.value);
  loadCompanyList();
  visible.value = true;
};

const handleValuechange = () => {
  inputValue.value = props.value;
};

watch(
  () => props.value,
  async () => {
    await nextTick();
    handleValuechange();
  },
  { immediate: true },
);

const blur = () => {
  visible.value = false;
};

const chooseItem = (op: any) => {
  emit('input', op.label);
  companyList.value = [];
  blur();
};

onMounted(() => {
  document.body.addEventListener('click', blur);
});

onBeforeUnmount(() => {
  document.body.removeEventListener('click', blur);
});
</script>

<style lang="scss">
@import '../assets/_scrollbar.scss';
.company-search {
  &__input {
    @apply border border-grey-60;
    height: 32px;
    line-height: 32px;
    padding-left: 15px;
    padding-right: 30px;
    margin-bottom: 4px;
    border-radius: 4px;
    outline: none;
    white-space: nowrap;
    overflow: hidden;
    &:empty:before {
      content: attr(placeholder);
      @apply text-grey-90;
    }
  }
  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100px;

    .patsnap-biz-icon {
      font-size: 24px;
      @apply text-grey-450;
    }
  }
  &__popper {
    padding: 0;
    margin-top: 0 !important;
    max-height: 132px;
    box-shadow: none;
    overflow: auto;
    .patsnap-ui-list {
      padding: 0;
      .patsnap-ui-list-item {
        padding: 4px 8px;
        &:hover {
          border-radius: 0;
        }
      }
    }
    &::-webkit-scrollbar {
      width: 14px;
      height: 14px;
      background-color: transparent;
      background-clip: content-box;
    }
    &::-webkit-scrollbar-button {
      height: 0;
      width: 0;
    }
    &::-webkit-scrollbar-corner {
      background-color: transparent;
    }
    &::-webkit-scrollbar-thumb {
      width: 4px;
      height: 40px;
      border-radius: 6px;
      background: #f4f5f7;
      border: 3px solid transparent;
      background-clip: content-box;
    }
    &::-webkit-scrollbar-track {
      background: #fff;
      border-left: 1px solid #dfe1e6;
    }
  }
}
</style>
